import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowRight, CalendarDays, CircleDollarSign, FileDown, Play, Plus, Users2, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const money2 = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });

const STATUS_META = {
  active: { label: "Activo", color: "#238a65", bg: "#dcf7ed" },
  missing_info: { label: "Faltan datos", color: "#a97a11", bg: "#fff2c9" },
  on_vacation: { label: "Vacaciones", color: "#4169e1", bg: "#eef4ff" },
  inactive: { label: "Inactivo", color: "#6b7280", bg: "#f3f4f6" },
};

const FREQ_LABEL = {
  weekly: "Semanal", biweekly: "Quincenal", monthly: "Mensual",
  hourly: "Por hora", commission: "Comisión", mixed: "Mixto",
};

const kpiStyle = (bg = "#f9fafb") => ({
  background: bg,
  padding: "18px 20px",
  borderRadius: 18,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minHeight: 92,
  boxShadow: "0 4px 14px rgba(15,23,42,0.05)",
});

export default function PayrollDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [runs, setRuns] = useState([]);
  const [showRun, setShowRun] = useState(false);

  const load = () => {
    api.get("/payroll/dashboard", { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "No pudimos cargar la nómina."));
    api.get("/payroll/runs?limit=5", { withCredentials: true })
      .then((r) => setRuns(r.data || []))
      .catch(() => setRuns([]));
  };
  useEffect(load, []);

  const totals = data?.totals || {};
  const employees = data?.employees || [];

  return (
    <AppShell>
      <main className="page projects-page" data-testid="payroll-dashboard-page">
        <header className="expenses-head">
          <div>
            <small className="eyebrow">NÓMINA</small>
            <h1>Paga a tu equipo <em>sin dolores de cabeza.</em></h1>
            <p className="academia-subtitle">Agrega empleados con un pequeño wizard, corre nóminas con cálculos oficiales de ISR y descarga recibos.</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="expenses-add-button" onClick={() => navigate("/nomina/nuevo")} data-testid="new-employee-btn"><Plus size={17} /> Agregar empleado</button>
            {totals.active > 0 && (
              <button className="cash-btn primary" onClick={() => setShowRun(true)} data-testid="run-payroll-btn" style={{ padding: "12px 18px", borderRadius: 999 }}><Play size={15} /> Correr nómina</button>
            )}
          </div>
        </header>

        {error && <p className="expenses-error" data-testid="payroll-error">{error}</p>}

        <section className="project-kpi-row" data-testid="payroll-kpis" style={{ marginBottom: 26 }}>
          <div className="kpi-card" style={kpiStyle()}><small>Empleados totales</small><b>{totals.total || 0}</b></div>
          <div className="kpi-card" style={kpiStyle("#dcf7ed")}><small>Activos</small><b style={{ color: "#238a65" }}>{totals.active || 0}</b></div>
          <div className="kpi-card" style={kpiStyle(totals.missing ? "#fff2c9" : "#f3f4f6")}>
            <small>Faltan datos</small>
            <b style={{ color: totals.missing ? "#a97a11" : "#6b7280" }}>{totals.missing || 0}</b>
          </div>
          <div className="kpi-card" style={kpiStyle("#eef4ff")}><small>Próxima nómina · {totals.next_date || "—"}</small><b style={{ color: "#4169e1" }}>{money.format(totals.upcoming_total || 0)}</b></div>
        </section>

        <section className="employees-list" data-testid="employees-list" style={{ background: "#fff", borderRadius: 22, padding: 22, boxShadow: "0 8px 30px rgba(15,23,42,0.06)", marginBottom: 26 }}>
          <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h2 style={{ fontSize: 20, margin: 0 }}>Tu equipo</h2>
            <span style={{ color: "#6b7280", fontSize: 13 }}>{employees.length} persona(s)</span>
          </header>

          {employees.length === 0 ? (
            <div className="expenses-empty" data-testid="employees-empty" style={{ padding: "26px 0" }}>
              <Users2 size={44} color="#4169e1" />
              <h2>Aún no tienes empleados.</h2>
              <p>Agrega a tu primera persona en 4 pasitos sencillos.</p>
              <button type="button" onClick={() => navigate("/nomina/nuevo")} data-testid="employees-empty-add">Agregar empleado</button>
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
              {employees.map((emp) => {
                const meta = STATUS_META[emp.status] || STATUS_META.active;
                return (
                  <li key={emp.id}>
                    <Link
                      to={`/nomina/${emp.id}`}
                      data-testid={`employee-card-${emp.id}`}
                      style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "#f9fafb", borderRadius: 14, textDecoration: "none", color: "inherit", transition: "background 0.15s" }}
                    >
                      <div style={{ width: 42, height: 42, borderRadius: "50%", background: "#4169e1", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700 }}>
                        {(emp.first_name || "?")[0]?.toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <b style={{ display: "block" }}>{emp.display_name}</b>
                        <small style={{ color: "#6b7280" }}>{emp.position || "Sin puesto"} · {FREQ_LABEL[emp.frequency] || emp.frequency}</small>
                      </div>
                      <div style={{ textAlign: "right", minWidth: 130 }}>
                        <b>{money2.format(emp.gross_salary || 0)}</b>
                        <div style={{ marginTop: 4 }}>
                          <span style={{ background: meta.bg, color: meta.color, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{meta.label}</span>
                        </div>
                      </div>
                      <ArrowRight size={16} color="#9ca3af" />
                    </Link>
                    {emp.missing_fields?.length > 0 && (
                      <small style={{ display: "flex", gap: 6, alignItems: "center", color: "#a97a11", padding: "6px 16px 0" }}>
                        <AlertCircle size={12} /> Faltan: {emp.missing_fields.join(", ")}
                      </small>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {runs.length > 0 && (
          <section data-testid="payroll-runs" style={{ background: "#fff", borderRadius: 22, padding: 22, boxShadow: "0 8px 30px rgba(15,23,42,0.06)" }}>
            <h2 style={{ fontSize: 20, margin: "0 0 16px" }}>Historial de nóminas</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
              {runs.map((r) => (
                <li key={r.id} data-testid={`payroll-run-${r.id}`} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", background: "#f9fafb", borderRadius: 12 }}>
                  <CalendarDays size={18} color="#4169e1" />
                  <div style={{ flex: 1 }}>
                    <b>Periodo {r.period}</b>
                    <div style={{ color: "#6b7280", fontSize: 13 }}>{r.items?.length || 0} empleado(s) · {new Date(r.created_at).toLocaleDateString("es-MX")}</div>
                  </div>
                  <div style={{ textAlign: "right", marginRight: 12 }}>
                    <b>{money2.format(r.totals?.net || 0)}</b>
                    <div style={{ color: "#6b7280", fontSize: 12 }}>Neto a pagar</div>
                  </div>
                  <a
                    href={`${api.defaults.baseURL}/exports/payroll/${r.id}/pdf`}
                    target="_blank" rel="noreferrer"
                    className="cash-btn ghost"
                    data-testid={`download-run-pdf-${r.id}`}
                    style={{ padding: "8px 12px", fontSize: 13 }}
                    title="Descargar reporte PDF"
                  ><FileDown size={14} /> PDF</a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {showRun && <RunPayrollModal onClose={() => { setShowRun(false); load(); }} />}
      </main>
    </AppShell>
  );
}

function RunPayrollModal({ onClose }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.get("/payroll/preview", { withCredentials: true })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e.response?.data?.detail || "No pudimos calcular la nómina."));
  }, []);

  const confirm = async () => {
    setSaving(true); setError("");
    try {
      await api.post("/payroll/run", { period }, { withCredentials: true });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos guardar la nómina.");
      setSaving(false);
    }
  };

  const totals = preview?.totals || {};

  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="run-payroll-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(720px, 96vw)" }}>
        <header>
          <div>
            <small>CORRER NÓMINA</small>
            <b>Vista previa</b>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </header>
        <div style={{ padding: "0 24px 20px" }}>
          <label>Fecha del periodo<input type="date" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="run-period" /></label>

          {!preview && !error && <p style={{ color: "#6b7280" }}>Calculando…</p>}
          {error && <p className="expenses-error" data-testid="run-error">{error}</p>}

          {preview && preview.items.length === 0 && (
            <div style={{ padding: 20, background: "#fff2c9", borderRadius: 12, color: "#a97a11" }}>
              <AlertCircle size={20} /> No hay empleados activos con datos completos. Agrega o completa la información de tus empleados primero.
            </div>
          )}

          {preview && preview.items.length > 0 && (
            <>
              <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="run-preview-table">
                  <thead style={{ background: "#f3f4f6", position: "sticky", top: 0 }}>
                    <tr>
                      <th style={{ padding: "8px 10px", textAlign: "left" }}>Empleado</th>
                      <th style={{ padding: "8px 10px", textAlign: "right" }}>Bruto</th>
                      <th style={{ padding: "8px 10px", textAlign: "right" }}>ISR</th>
                      <th style={{ padding: "8px 10px", textAlign: "right" }}>IMSS</th>
                      <th style={{ padding: "8px 10px", textAlign: "right", color: "#238a65" }}>Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.map((it) => (
                      <tr key={it.employee_id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 10px" }}>{it.employee_name}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>{money2.format(it.gross)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: "#d7565d" }}>{money2.format(it.isr)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: "#d7565d" }}>{money2.format(it.imss)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#238a65" }}>{money2.format(it.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="project-kpi-row" style={{ marginTop: 16 }}>
                <div className="kpi-card" style={kpiStyle()}><small>Bruto</small><b>{money2.format(totals.gross || 0)}</b></div>
                <div className="kpi-card" style={kpiStyle("#fff2c9")}><small>ISR</small><b style={{ color: "#a97a11" }}>{money2.format(totals.isr || 0)}</b></div>
                <div className="kpi-card" style={kpiStyle("#eef4ff")}><small>Costo total (patrón)</small><b style={{ color: "#4169e1" }}>{money2.format(totals.employer_cost || 0)}</b></div>
                <div className="kpi-card" style={kpiStyle("#dcf7ed")}><small>Neto a pagar</small><b style={{ color: "#238a65" }}>{money2.format(totals.net || 0)}</b></div>
              </div>

              <p style={{ color: "#6b7280", fontSize: 12, marginTop: 12 }}>
                <CircleDollarSign size={13} /> ISR calculado con tablas oficiales del Art. 96 LISR. IMSS e INFONAVIT son estimados amigables.
              </p>
            </>
          )}
        </div>
        <div className="cash-modal-actions">
          <button type="button" onClick={onClose} className="cash-btn ghost">Cancelar</button>
          {preview && preview.items.length > 0 && (
            <button type="button" onClick={confirm} className="cash-btn primary" disabled={saving} data-testid="run-confirm-btn">
              {saving ? "Guardando…" : "Confirmar nómina"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
